/* global store */
/* global REPEAT_VIEW */

import { getElementFromEvent } from 'Render/utils/index';
import { fireTracking, fireThirdPartyTracking } from 'Tracking/index';

import {
  toMilliseconds,
} from 'Tracking/selectors';

import {
  AD_VIDEO_PERCENTAGE,
  AD_VIDEO_SEEK_TIME,
  AD_VIDEO_MUTED,
  AD_VIDEO_VOLUME_PERCENTAGE,
  AD_VIDEO_TIME,
  AD_VIDEO_DURATION,
  AD_COMPONENT_ID,
  AD_COMPONENT_NAME,
  AD_COMPONENT_TYPE,
} from 'Tracking/constants/trackingNames';

import {
  VIDEO_RESTART,
  VIDEO_START,
  VIDEO_PERCENT_PLAYED,
  VIDEO_SEEK_START,
  VIDEO_SEEK_END,
  VIDEO_END,
  VIDEO_PLAY,
  VIDEO_PAUSE,
  VIDEO_SOUND,
  VIDEO_ERROR,
  VIDEO_ENTER_FULLSCREEN,
  VIDEO_EXIT_FULLSCREEN,
} from 'Tracking/constants/events';

import {
  VIDEO,
} from 'Tracking/constants/componentTypes';

export const VIDEO_COMPLETION_REPORTING_INTERVAL = 25;

const getTrackingHelper = (id) => {
  const { trackingHelpers = {} } = store.getState();
  return trackingHelpers[id] || {};
};

const updateTrackingHelper = (id, trackingHelpers) => {
  store.dispatch({
    type: 'UPDATE_TRACKING_HELPER',
    id,
    trackingHelpers,
  });
};

const getVideoParams = video => ({
  currentTime: Math.round(video.currentTime),
  duration: Math.round(video.duration),
  percentPlayed: Math.round((video.currentTime / video.duration) * 100),
  volume: Math.round(video.volume),
  muted: video.muted,
});

const enrichFromVideo = (video, additionalTrackedInfo) => {
  const tracking = additionalTrackedInfo || {};
  const { currentTime, duration, volume, muted } = getVideoParams(video);
  const componentId = video.getAttribute('data-id') || null;
  const componentName = video.getAttribute('data-name') || null;
  tracking[AD_VIDEO_MUTED] = (volume === 0 || muted);
  tracking[AD_VIDEO_VOLUME_PERCENTAGE] = (muted ? 0 : volume) * 100;
  tracking[AD_VIDEO_TIME] = toMilliseconds(currentTime);
  tracking[AD_VIDEO_DURATION] = toMilliseconds(duration);
  tracking[AD_COMPONENT_ID] = componentId;
  tracking[AD_COMPONENT_NAME] = componentName;
  tracking[AD_COMPONENT_TYPE] = VIDEO;
  return tracking;
};

const getVideoElementWithTrackingHelperAndId = (e) => {
  const video = getElementFromEvent(e);
  const id = video.id;
  const uid = video.dataset.id;
  return { video, id, uid, trackingHelper: getTrackingHelper(id) };
};

export const loadedDataTrackingEvent = (e) => {
  const { video, id } = getVideoElementWithTrackingHelperAndId(e);
  updateTrackingHelper(id, {
    muted: video.volume === 0 || video.muted,
  });
};

export const timelineUpdateTrackingEvent = (e) => {
  const { thirdPartyTracking } = store.getState();
  const { video, id, uid, trackingHelper } = getVideoElementWithTrackingHelperAndId(e);
  let {
    previouslyTrackedVideoCompletionRates = [],
  } = trackingHelper;
  let {
    seeking = false,
    seekStart = 0,
    seekEnd = 0,
    hasStarted = false,
    hasEnded = false,
  } = trackingHelper;

  const { currentTime, duration, percentPlayed } = getVideoParams(video);

  if (hasStarted && hasEnded && percentPlayed === 0) {
    previouslyTrackedVideoCompletionRates = [];
  }

  const durationOffset = ((duration * 0.25) > 2) ? (duration * 0.85) : duration - 2;

  for (let percent = 0; percent <= 99; percent += VIDEO_COMPLETION_REPORTING_INTERVAL) {
    if (!hasStarted && (currentTime >= 1)) {
      hasStarted = true;
      fireTracking(VIDEO_START, enrichFromVideo(video));
      if (thirdPartyTracking.video && thirdPartyTracking.video.all && thirdPartyTracking.video.all[VIDEO_START]) fireThirdPartyTracking(thirdPartyTracking.video.all[VIDEO_START]);
      if (thirdPartyTracking.video && thirdPartyTracking.video[uid] && thirdPartyTracking.video[uid][VIDEO_START]) fireThirdPartyTracking(thirdPartyTracking.video[uid][VIDEO_START]);
    }
    if (percentPlayed >= percent && previouslyTrackedVideoCompletionRates.indexOf(percent) === -1) {
      if (percent === 0 && percentPlayed > 0) {
        if (hasStarted && hasEnded) {
          fireTracking(VIDEO_RESTART, enrichFromVideo(video));
          if (thirdPartyTracking.video && thirdPartyTracking.video.all && thirdPartyTracking.video.all[VIDEO_RESTART]) fireThirdPartyTracking(thirdPartyTracking.video.all[VIDEO_RESTART]);
          if (thirdPartyTracking.video && thirdPartyTracking.video[uid] && thirdPartyTracking.video[uid][VIDEO_RESTART]) fireThirdPartyTracking(thirdPartyTracking.video[uid][VIDEO_RESTART]);
          hasEnded = false;
        }
      } else if (percentPlayed !== 0) {
        fireTracking(VIDEO_PERCENT_PLAYED,
          enrichFromVideo(video, { [AD_VIDEO_PERCENTAGE]: percent }));
        if (thirdPartyTracking.video && thirdPartyTracking.video.all && thirdPartyTracking.video.all[percent]) fireThirdPartyTracking(thirdPartyTracking.video.all[percent]);
        if (thirdPartyTracking.video && thirdPartyTracking.video[uid] && thirdPartyTracking.video[uid][percent]) fireThirdPartyTracking(thirdPartyTracking.video[uid][percent]);
      }
      if (percentPlayed > 0) {
        previouslyTrackedVideoCompletionRates.push(percent);
      }
    }
  }

  if ((currentTime >= durationOffset) && !hasEnded) {
    hasEnded = true;
    fireTracking(VIDEO_END, enrichFromVideo(video));
    if (thirdPartyTracking.video && thirdPartyTracking.video.all && thirdPartyTracking.video.all[VIDEO_END]) fireThirdPartyTracking(thirdPartyTracking.video.all[VIDEO_END]);
    if (thirdPartyTracking.video && thirdPartyTracking.video[uid] && thirdPartyTracking.video[uid][VIDEO_END]) fireThirdPartyTracking(thirdPartyTracking.video[uid][VIDEO_END]);
  }

  seekStart = seekEnd;
  seekEnd = currentTime;
  if (Math.abs(seekStart - seekEnd) > 1) {
    seeking = true;
    fireTracking(VIDEO_SEEK_START, enrichFromVideo(video, {
      [AD_VIDEO_SEEK_TIME]: seekStart * 1000,
    }));
    fireTracking(VIDEO_SEEK_END, enrichFromVideo(video, {
      [AD_VIDEO_SEEK_TIME]: seekEnd * 1000,
    }));
  }

  updateTrackingHelper(id, {
    previouslyTrackedVideoCompletionRates,
    seekStart,
    seekEnd,
    seeking,
    hasStarted,
    hasEnded,
  });
};

export const playTrackingEvent = (e) => {
  const { video, id } = getVideoElementWithTrackingHelperAndId(e);
  fireTracking(VIDEO_PLAY, enrichFromVideo(video));
  if (REPEAT_VIEW === 'restart') {
    updateTrackingHelper(id, {
      seeking: false,
      previouslyTrackedVideoCompletionRates: [],
    });
  }
};

export const pauseTrackingEvent = (e) => {
  const { video, trackingHelper } = getVideoElementWithTrackingHelperAndId(e);
  const { seeking = false } = trackingHelper;
  const { currentTime, duration } = getVideoParams(video);
  if (currentTime !== duration && !seeking) {
    fireTracking(VIDEO_PAUSE, enrichFromVideo(video));
  }
};

export const volumeChangeTrackingEvent = (e) => {
  const { video, id, trackingHelper } = getVideoElementWithTrackingHelperAndId(e);
  let { lastMuted = false } = trackingHelper;
  const { volume, muted } = getVideoParams(video);
  if (
    (volume === 0 && !lastMuted)
    ||
    (volume !== 0 && lastMuted)
    ||
    (muted !== lastMuted)
  ) {
    lastMuted = (volume === 0 && !lastMuted) || muted;
    fireTracking(VIDEO_SOUND, enrichFromVideo(video));
  }
  updateTrackingHelper(id, { lastMuted });
};

export const errorTrackingEvent = (e) => {
  const { video } = getVideoElementWithTrackingHelperAndId(e);
  fireTracking(VIDEO_ERROR, enrichFromVideo(video));
};

export const fullScreenTrackingEvent = (e) => {
  const { video, id, trackingHelper } = getVideoElementWithTrackingHelperAndId(e);
  const { isFullscreen = false } = trackingHelper;
  if (!isFullscreen) {
    fireTracking(VIDEO_ENTER_FULLSCREEN, enrichFromVideo(video));
  } else {
    fireTracking(VIDEO_EXIT_FULLSCREEN, enrichFromVideo(video));
  }
  updateTrackingHelper(id, { isFullscreen: !isFullscreen });
};



// WEBPACK FOOTER //
// ./app/javascript/packs/App/Render/tracking/actions/video.js